import type { UsersApiDatasource } from '../datasources/users-api.datasource';
import type {
  ListUsersInput,
  UsersListResponse,
  UserDetail,
  ProvisionUserInput,
  ProvisionResult,
  ResendInviteResult,
  CreatePlatformProfileInput,
  ProvisionScopedUserInput,
  ProvisionScopedUserResult,
  ApplyPerkGrantBatchInput,
  PerkGrantBatchResult,
  UpdateUserInput,
  IdentityEntityType,
  ListIdentityOperationalAlertsInput,
  OperationalAlertFacet,
  IdentityOperationalAlertStatus,
  CloseIdentityOperationalAlertInput,
} from '../../domain/entities/user.entity';

export class UsersRepositoryImpl {
  private api: UsersApiDatasource;

  constructor(api: UsersApiDatasource) {
    this.api = api;
  }

  async list(input?: ListUsersInput): Promise<UsersListResponse> {
    return this.api.list(input);
  }

  async getById(userId: string): Promise<UserDetail> {
    return this.api.getById(userId);
  }

  async provision(input: ProvisionUserInput): Promise<ProvisionResult> {
    return this.api.provision(input);
  }

  async resendInvite(accountId: string): Promise<ResendInviteResult> {
    return this.api.resendInvite(accountId);
  }

  async createPlatformProfile(
    input: CreatePlatformProfileInput,
  ): Promise<void> {
    return this.api.createPlatformProfile(input);
  }

  async provisionScopedUser(
    input: ProvisionScopedUserInput,
  ): Promise<ProvisionScopedUserResult> {
    return this.api.provisionScopedUser(input);
  }

  async unlinkIdentity(accountId: string, method: string): Promise<void> {
    return this.api.unlinkIdentity(accountId, method);
  }

  async activateProfile(profileId: string): Promise<void> {
    return this.api.activateProfile(profileId);
  }

  async deactivateProfile(profileId: string): Promise<void> {
    return this.api.deactivateProfile(profileId);
  }

  async listGrantedPerks(profileId: string) {
    return this.api.listGrantedPerks(profileId);
  }

  async listEffectivePerks(profileId: string, scopeId: string) {
    return this.api.listEffectivePerks(profileId, scopeId);
  }

  async applyPerkGrantBatch(
    input: ApplyPerkGrantBatchInput,
  ): Promise<PerkGrantBatchResult> {
    return this.api.applyPerkGrantBatch(input);
  }

  async updateUser(userId: string, data: UpdateUserInput): Promise<void> {
    return this.api.updateUser(userId, data);
  }

  async resetPassword(accountId: string): Promise<void> {
    return this.api.resetPassword(accountId);
  }

  async setTemporaryPassword(
    accountId: string,
    password: string,
  ): Promise<void> {
    return this.api.setTemporaryPassword(accountId, password);
  }

  async resendVerification(accountId: string): Promise<void> {
    return this.api.resendVerification(accountId);
  }

  async getEntityEvents(
    entityType: IdentityEntityType,
    entityId: string,
    params: { page: number; perPage: number },
  ) {
    return this.api.getEntityEvents(entityType, entityId, params);
  }

  async getEntityEventsByCorrelationId(
    correlationId: string,
    params: { page: number; perPage: number },
  ) {
    return this.api.getEntityEventsByCorrelationId(correlationId, params);
  }

  listOperationalAlertFacets(
    status?: IdentityOperationalAlertStatus,
  ): Promise<OperationalAlertFacet[]> {
    return this.api.listOperationalAlertFacets(status);
  }

  async getDuplicateDocument(userId: string) {
    return this.api.getDuplicateDocument(userId);
  }

  async listOperationalAlerts(input?: ListIdentityOperationalAlertsInput) {
    return this.api.listOperationalAlerts(input);
  }

  async resolveOperationalAlert(
    id: string,
    input: CloseIdentityOperationalAlertInput,
  ) {
    return this.api.resolveOperationalAlert(id, input);
  }

  async dismissOperationalAlert(
    id: string,
    input: CloseIdentityOperationalAlertInput,
  ) {
    return this.api.dismissOperationalAlert(id, input);
  }

  async getProfilePerkEvents(
    profileId: string,
    params: { page: number; perPage: number },
  ) {
    return this.api.getProfilePerkEvents(profileId, params);
  }
}
